import PropTypes from 'prop-types'
import React, { PureComponent } from 'react'
import classnames from 'classnames'
import ImmutableProptypes from 'react-immutable-proptypes'
import { Map } from 'immutable'
import update from 'immutability-helper'
import { DragDropContext } from 'react-dnd'
import HTML5Backend from 'react-dnd-html5-backend'
import { connect as reduxConnect } from 'react-redux'
import Modal from 'react-modal'
import { browserHistory } from 'react-router'
import ReactPlayer from 'react-player'
import Select from 'react-select'

import {
  getMindfulBreaks,
  setMindfulBreaksOrder,
  deleteMindfulBreak,
  createMindfulBreak,
  editMindfulBreak,
  disableReminderType,
} from '_modules/mindful-breaks/actions'
import { updateLastActivityAt } from '_modules/user/actions'
import { clearNotifications } from '_modules/notification/actions'
import { startTask } from '_modules/current-task/actions'
import { startTimer, decrementCounter } from '_modules/counter/actions'
import { getMindfulBreakDuration } from '_utils/counter'
import { planTypes } from '_utils/planType'
import { objectToArray, createFieldMissingErrorMessage, deepCopyJSON } from '_utils/helperFunction'
import { sortByOrder } from '_utils/sort'
import Dropdown from '_components/dropdown'
import DurationInput from '_components/durationInput'
import { USER_STATUS_TYPES } from '_utils/userStatusType'
import Socket from '_utils/socket'

import OrderingCard from './ordering-card'
import styles from './styles.css'

const menuItems = [
  { key: '1', name: 'After Time Interval' },
  { key: '2', name: 'After Task Completed' },
]

const mapStateToProps = ({ mindfulBreaks, counter, user, settings }) => ({
  mindfulBreaks,
  counter,
  user,
  settings,
})

const mapDispatchToProps = {
  getMindfulBreaks,
  setMindfulBreaksOrder,
  deleteMindfulBreak,
  createMindfulBreak,
  editMindfulBreak,
  disableReminderType,
  startTimer,
  startTask,
  decrementCounter,
  clearNotifications,
  updateLastActivityAt,
}

@DragDropContext(HTML5Backend)
class MindfulBreaks extends PureComponent {
  static propTypes = {
    mindfulBreaks: ImmutableProptypes.map,
    counter: ImmutableProptypes.map,
    user: ImmutableProptypes.map,
    settings: ImmutableProptypes.map,
    getMindfulBreaks: PropTypes.func.isRequired,
    setMindfulBreaksOrder: PropTypes.func.isRequired,
    deleteMindfulBreak: PropTypes.func.isRequired,
    createMindfulBreak: PropTypes.func.isRequired,
    editMindfulBreak: PropTypes.func.isRequired,
    disableReminderType: PropTypes.func.isRequired,
    startTimer: PropTypes.func.isRequired,
    startTask: PropTypes.func.isRequired,
    decrementCounter: PropTypes.func.isRequired,
    clearNotifications: PropTypes.func.isRequired,
    updateLastActivityAt: PropTypes.func.isRequired,
  }

  static defaultProps = {
    mindfulBreaks: new Map(),
    counter: new Map(),
    user: new Map(),
    settings: new Map(),
  }

  state = {
    cards: [],
    isModalOpen: false,
    modalName: '',
    modalDuration: 0,
    modalId: undefined,
    reminderType: 1,
    reminderActive: false,
    planType: 100,
    inProgress: false,
    embedLink: '',
    validEmbedLink: false,
    selectedOption: null,
  }

  componentWillMount() {
    Modal.setAppElement('body')
    this.getState(this.props)
  }

  componentDidMount() {
    this.props.getMindfulBreaks()
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.user.get('lastActivityAt') !== nextProps.user.get('lastActivityAt')) {
      this.props.getMindfulBreaks()
    }
    this.getState(nextProps)
  }

  componentWillUnmount() {
    this.props.clearError()
  }

  onAddClick = e => {
    e.preventDefault()

    this.setState(
      {
        isModalOpen: true,
        modalName: '',
        modalDuration: 0,
        modalId: undefined,
        reminderType: 1,
        reminderActive: false,
        embedLink: '',
        validEmbedLink: false,
      },
      () => {
        this.nameInput.focus()
      }
    )
  }

  onRefreshClick = () => {
    this.props.getMindfulBreaks()
  }

  onKeyPress = event => {
    if (event.key === 'Enter') {
      this.onConfirmModal(event)
    }
  }

  onModalChange = e => {
    e.preventDefault()

    let { id, value } = e.target

    this.setState({
      [id]: value,
    })
  }

  onEmbedVideoLinkChange = e => {
    e.preventDefault()

    let { id, value } = e.target

    this.setState({
      [id]: value,
      validEmbedLink: ReactPlayer.canPlay(value),
    })
  }

  onInputChange = e => {
    const { id } = e.target
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value

    this.setState({
      [id]: value,
    })
  }

  onCancelModal = e => {
    e.preventDefault()
    this.props.clearError()

    this.setState({
      isModalOpen: false,
      modalName: '',
      modalDuration: 0,
      modalId: undefined,
      reminderType: 1,
      reminderActive: false,
      embedLink: '',
      validEmbedLink: false,
    })
  }

  onConfirmModal = e => {
    e.preventDefault()
    this.props.clearError()

    const { modalName, modalDuration, modalId } = this.state

    if (!modalName || !modalDuration) {
      this.props.displayError(
        createFieldMissingErrorMessage({
          breakName: modalName,
          breakDuration: modalDuration,
        })
      )
      return
    }

    if (modalDuration < 1) {
      this.props.displayError('Break must be at least 1 minute')
      return
    }

    if (modalId) {
      this.modifyMindfulBreak()
    } else {
      this.createMindfulBreak()
    }

    this.setState({
      isModalOpen: false,
    })
  }

  getState = props => {
    const cards = objectToArray(props.mindfulBreaks.toJS()).sort(sortByOrder)
    const planType = props.user.toJS().planType * 1
    const inProgress = props.counter.toJS().status === 'in progress'
    this.setState({ cards, planType, inProgress })
  }

  playCard = id => {
    const { mindfulBreaks, counter, settings } = this.props
    const { cards } = this.state

    if (counter.get('intervalId') !== null) {
      return
    }

    const card = cards.filter(c => c._id === id)[0]
    const name = card.name
    const duration = getMindfulBreakDuration(mindfulBreaks, name) * 60

    const intervalId = setInterval(this.timer, 1000)
    this.props.startTimer(intervalId, name, duration, 2, card.embedLink)

    const payload = {
      name,
      type: 2,
      duration,
    }
    this.props.startTask(payload)
    this.props.clearNotifications()

    if (settings.get('autoStatus')) {
      Socket.emit('changeUserStatus', USER_STATUS_TYPES.FOCUSED)
    }

    browserHistory.push('/')
  }

  editCard = id => {
    const { cards } = this.state
    const card = cards.filter(c => c._id === id)[0]

    const selectedOption = this.selectOptions.find(option => option.value === card.reminderType)

    this.setState(
      {
        isModalOpen: true,
        modalName: card.name,
        modalDuration: card.duration,
        modalId: card._id,
        reminderActive: card.reminderActive,
        reminderType: card.reminderType,
        embedLink: card.embedLink,
        selectedOption,
      },
      () => {
        this.nameInput.focus()
      }
    )
  }

  toggleModal = () => {
    this.props.clearError()
    this.setState({
      isModalOpen: !this.state.isModalOpen,
    })
  }

  saveOrdering = () => {
    const { cards } = this.state

    const payload = cards.reduce((obj, item, index) => {
      const newObj = { ...obj }
      newObj[item._id] = index
      return newObj
    }, {})

    this.props.setMindfulBreaksOrder(payload)
    this.props.updateLastActivityAt({
      lastActivityAt: new Date(),
    })
  }

  moveCard = (dragIndex, hoverIndex) => {
    const { cards } = this.state
    const dragCard = cards[dragIndex]

    this.setState(
      update(this.state, {
        cards: {
          $splice: [[dragIndex, 1], [hoverIndex, 0, dragCard]],
        },
      })
    )
  }

  sortAssetByPosition = (a, b) => {
    if (a.get('order') < b.get('order')) return -1
    if (a.get('order') > b.get('order')) return 1
    return 0
  }

  deleteCard = id => {
    this.props.deleteMindfulBreak(id)
    this.props.updateLastActivityAt({
      lastActivityAt: new Date(),
    })
  }

  createMindfulBreak = () => {
    const {
      modalName: name,
      modalDuration: duration,
      cards,
      reminderType,
      reminderActive,
      embedLink,
    } = this.state

    const order = cards.reduce((prevVal, card) => {
      if (card.order > prevVal) {
        return card.order
      }
      return prevVal
    }, 0)

    const payload = {
      name,
      duration,
      order: order + 1,
      reminderType,
      reminderActive,
      embedLink,
    }
    if (reminderActive) this.props.disableReminderType({ reminderType })
    this.props.createMindfulBreak(payload)
    this.props.updateLastActivityAt({
      lastActivityAt: new Date(),
    })
  }

  modifyMindfulBreak = () => {
    const {
      modalName: name,
      modalDuration: duration,
      modalId: id,
      cards,
      reminderType,
      reminderActive,
      embedLink,
    } = this.state

    const card = cards.filter(c => c._id === id)[0]
    const payload = {
      name,
      duration: Number(duration),
      order: card.order,
      reminderType,
      reminderActive,
      embedLink,
    }

    if (reminderActive) this.props.disableReminderType({ reminderType })
    this.props.editMindfulBreak(id, payload)
    this.props.updateLastActivityAt({
      lastActivityAt: new Date(),
    })
  }

  updateBreakInfo = () => {
    this.toggleModal()
  }

  timer = () => {
    this.props.decrementCounter()
  }

  checkLock = () => {
    const { planType } = this.state
    if (planType < planTypes.pro) browserHistory.push('/subscribe')
  }

  onDurationChange = modalDuration => {
    this.setState({
      modalDuration,
    })
  }

  renderCards = cards =>
    cards.map((card, index) => (
      <li
        key={card._id}
        className={classnames(
          styles['Grid-cell'],
          styles.pause,
          this.state.inProgress &&
            this.props.counter.get('name').toLowerCase() === card.name.toLowerCase() &&
            styles.disabled
        )}
      >
        <OrderingCard
          index={index}
          moveCard={this.moveCard}
          id={card._id}
          saveOrdering={this.saveOrdering}
          deleteCard={this.deleteCard}
          editCard={this.editCard}
          playCard={this.playCard}
          reminderActive={card.reminderActive || false}
          name={card.name}
          duration={card.duration}
        />
      </li>
    ))

  divRef = null

  handleChange = selectedOption => {
    this.setState({ selectedOption, reminderType: selectedOption.value })
  }

  selectOptions = [
    { value: 1, label: 'After Time Interval' },
    { value: 2, label: 'After Task Completed' },
  ]

  render() {
    const {
      cards,
      isModalOpen,
      modalDuration,
      modalName,
      reminderActive,
      planType,
      embedLink,
      validEmbedLink,
      selectedOption,
    } = this.state

    const noBreaks = cards.length === 0

    const customStyles = {
      control: (provided, state) => ({
        ...provided,
        backgroundColor: 'auto',
        border: ' 1px solid rgba(255,255,255,0.3)',
      }),
      placeholder: (provided, state) => ({ ...provided, fontSize: 16, color: 'white' }),
      singleValue: (provided, state) => ({ ...provided, fontSize: 16, color: 'white' }),
      input: (provided, state) => ({ ...provided, fontSize: 16, color: 'white' }),
      menu: (provided, state) => ({
        ...provided,
        backgroundColor: 'rgba(80,95,172,0.8)',
        fontSize: 16,
        color: 'white',
      }),
      option: (provided, { isFocused }) => {
        return {
          ...provided,
          color: isFocused ? 'black' : 'white',
        }
      },
    }

    return (
      <div
        ref={div => (this.divRef = div)}
        className={classnames(styles.main, styles.Grid, styles['Grid--alignCenter'])}
      >
        <div className={classnames(styles['Grid-cell'], styles.title)}>Mindful Breaks</div>
        <div
          className={classnames(styles['Grid-cell'], styles['list-container'])}
          style={{ display: 'flex', flexDirection: 'column' }}
        >
          {noBreaks ? (
            <div
              style={{
                color: 'rgba(255,255,255,0.5)',
                padding: 30,
                fontSize: 15,
                alignSelf: 'center',
              }}
            >
              Create a break.
            </div>
          ) : (
            <ul className={classnames(styles.list)}>{this.renderCards(cards)}</ul>
          )}
        </div>
        <div className={classnames(styles['Grid-cell'], styles['u-sizeFit'])}>
          <button className={classnames(styles['add-button'])} onClick={this.onAddClick}>
            Add New
          </button>
          <button className={classnames(styles['add-button'])} onClick={this.onRefreshClick}>
            Refresh
          </button>
        </div>
        <Modal
          isOpen={isModalOpen}
          className={classnames(styles.modal)}
          overlayClassName={classnames(styles.overlay)}
          onRequestClose={this.toggleModal}
          shouldCloseOnOverlayClick
          bodyOpenClassName={classnames(styles['disable-body-scroll'])}
        >
          <div className={classnames(styles.Grid, styles['Grid--alignCenter'])}>
            <input
              ref={input => {
                this.nameInput = input
              }}
              type="text"
              id="modalName"
              placeholder="Name"
              value={modalName}
              onChange={this.onModalChange}
              className={classnames(styles['Grid-cell'], styles['u-sizeFit'], styles.input)}
              onKeyPress={this.onKeyPress}
            />
            <DurationInput
              ref={input => {
                this.durationInput = input
              }}
              type="text"
              id="modalDuration"
              placeholder="Minutes"
              value={modalDuration}
              onChange={this.onDurationChange}
              className={classnames(styles['Grid-cell'], styles['u-sizeFit'], styles.input)}
              onKeyPress={this.onKeyPress}
            />

            <div
              role="link"
              tabIndex={0}
              className={classnames(styles['Grid-cell'], styles.lockLink)}
              onClick={this.checkLock}
            >
              <div
                className={classnames(styles['Grid-cell'], [
                  planType < planTypes.pro && styles.locked,
                ])}
              >
                <input
                  id="embedLink"
                  type="text"
                  placeholder="Video URL (Optional)"
                  value={embedLink}
                  onChange={this.onEmbedVideoLinkChange}
                  style={{
                    borderBottomWidth: validEmbedLink || embedLink === '' ? 1 : 2,
                    borderBottomColor:
                      validEmbedLink || embedLink === '' ? 'rgba(255, 255, 255, 0.3)' : 'red',
                  }}
                  className={classnames(styles['Grid-cell'], styles['u-sizeFit'], styles.input)}
                />
                <label
                  className={classnames(
                    styles['Grid-cell'],
                    styles['u-sizeFit'],
                    styles['is-reminder']
                  )}
                >
                  <input
                    id="reminderActive"
                    type="checkbox"
                    checked={reminderActive}
                    onChange={this.onInputChange}
                    className={classnames(styles['sub-item'])}
                  />
                  <div className={classnames(styles['sub-item'])}>Set Reminder</div>
                </label>
                <Select
                  className={classnames(styles['Grid-cell'], styles['is-reminder'], styles.select)}
                  value={selectedOption}
                  onChange={this.handleChange}
                  options={this.selectOptions}
                  styles={customStyles}
                />
              </div>
              {planType < planTypes.pro && (
                <div className={classnames(styles['Grid-cell'], styles.lockIcon)}>
                  <i size={40} className={classnames('fa fa-lock')} />
                </div>
              )}
            </div>
            <button
              onClick={this.onConfirmModal}
              className={classnames(styles['Grid-cell'], styles['u-sizeFit'], styles.button)}
            >
              SAVE
            </button>
            <button
              onClick={this.onCancelModal}
              className={classnames(styles['Grid-cell'], styles['u-sizeFit'], styles.button)}
            >
              CANCEL
            </button>
          </div>
        </Modal>
      </div>
    )
  }
}

export default reduxConnect(mapStateToProps, mapDispatchToProps)(MindfulBreaks)



// WEBPACK FOOTER //
// ./src/views/mindful-breaks/index.js